import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { cardsApi } from "@/lib/cardsApi";
import { HOME_TOOLS } from "@/constants/testIds";

/**
 * Home-page bento showcasing the four things a signed-in user can
 * actually do on the site. Sits between the manifesto and the featured
 * trees so first-time visitors see the depth of the tools before the
 * social proof.
 */
const TOOLS = [
  {
    key: "tree",
    eyebrow: "Build",
    title: "Your family tree",
    body: "Add ancestors, siblings, spouses and children. Auto-arranged by generation, saved forever.",
    to: "/my-tree",
    cta: "Start your tree",
    accent: "text-tulsi",
    tone: "bg-gradient-to-br from-[#F1E9DA] to-[#E9DDC3]",
    illustration: (
      <svg viewBox="0 0 220 160" className="w-full h-full">
        <defs>
          <linearGradient id="tree-g" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#2D4A3E" stopOpacity="0.9" />
            <stop offset="1" stopColor="#2D4A3E" stopOpacity="0.4" />
          </linearGradient>
        </defs>
        {/* three generations */}
        <g stroke="url(#tree-g)" strokeWidth="1.4" fill="none" strokeLinecap="round">
          <path d="M 60 42 C 60 70 100 70 100 100" />
          <path d="M 160 42 C 160 70 120 70 120 100" />
          <path d="M 100 100 C 100 122 80 122 60 140" />
          <path d="M 120 100 C 120 122 140 122 160 140" />
        </g>
        {[[60, 30], [160, 30], [110, 100], [60, 140], [160, 140]].map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="18" fill="#FDFBF7" stroke="#2D4A3E" strokeWidth="1.2" />
            <text x={x} y={y + 5} textAnchor="middle" fontSize="14" fill="#2D4A3E" fontFamily="serif">
              {["A","B","C","D","E"][i]}
            </text>
          </g>
        ))}
      </svg>
    ),
  },
  {
    key: "cards",
    eyebrow: "Create",
    title: "A personal message card",
    body: "Birthdays, anniversaries, memorials — pick an occasion, upload a portrait, AI helps with words, download the keepsake.",
    to: "/cards",
    cta: "Open the card studio",
    accent: "text-saffron",
    tone: "bg-gradient-to-br from-[#F6D6C7] to-[#E86A33]/30",
    illustration: (
      <svg viewBox="0 0 220 160" className="w-full h-full">
        {/* mini card */}
        <rect x="60" y="16" width="100" height="128" rx="8" fill="#FDFBF7" stroke="#E86A33" strokeWidth="1.4" />
        <rect x="66" y="22" width="88" height="116" rx="4" fill="none" stroke="#F2A93B" strokeWidth="0.6" />
        <text x="110" y="46" textAnchor="middle" fontFamily="serif" fontSize="12" fill="#8B261D" fontStyle="italic">Happy Birthday</text>
        <ellipse cx="110" cy="88" rx="26" ry="32" fill="#F2A93B" opacity="0.35" stroke="#E86A33" />
        <line x1="80" y1="128" x2="140" y2="128" stroke="#E86A33" strokeWidth="0.8" />
        <text x="110" y="122" textAnchor="middle" fontFamily="serif" fontSize="7" fill="#0A0F0D">Meera Menon</text>
      </svg>
    ),
  },
  {
    key: "portraits",
    eyebrow: "Preserve",
    title: "A portrait gallery",
    body: "Upload photographs of the people in your archive. Reuse them across trees, memorials and cards.",
    to: "/my-portraits",
    cta: "Open your gallery",
    accent: "text-[#8B261D]",
    tone: "bg-gradient-to-br from-[#E8C3BE] to-[#8B261D]/25",
    illustration: (
      <svg viewBox="0 0 220 160" className="w-full h-full">
        {[0, 1, 2, 3].map((i) => {
          const x = 24 + i * 44;
          return (
            <g key={i}>
              <rect x={x} y={30 + (i % 2) * 10} width="40" height="52" rx="20" fill="#FDFBF7" stroke="#8B261D" strokeWidth="1.2" />
              <circle cx={x + 20} cy={44 + (i % 2) * 10} r="8" fill="#8B261D" opacity="0.35" />
              <rect x={x + 10} y={54 + (i % 2) * 10} width="20" height="18" rx="9" fill="#8B261D" opacity="0.35" />
            </g>
          );
        })}
      </svg>
    ),
  },
  {
    key: "heritage",
    eyebrow: "Explore",
    title: "Indian heritage, deeply",
    body: "98 topics — festivals, villages, gotras, traditions — with AI-written editorials and downloadable magazine cards.",
    to: "/indian-heritage",
    cta: "Browse heritage",
    accent: "text-tulsi",
    tone: "bg-gradient-to-br from-[#CBD8D0] to-[#2D4A3E]/25",
    illustration: (
      <svg viewBox="0 0 220 160" className="w-full h-full">
        <text x="30" y="60" fontFamily="serif" fontSize="42" fill="#2D4A3E">ॐ</text>
        <text x="88" y="94" fontFamily="serif" fontSize="28" fill="#E86A33">पर्व</text>
        <text x="150" y="60" fontFamily="serif" fontSize="34" fill="#8B261D">✳</text>
        <text x="50" y="130" fontFamily="serif" fontSize="14" fill="#2D4A3E" fontStyle="italic">98 topics</text>
      </svg>
    ),
  },
];

export function ToolsBento() {
  return (
    <section
      data-testid={HOME_TOOLS.root}
      className="relative py-24 md:py-32 bg-ivory-100/70 border-y border-ivory-300"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end mb-12">
          <div className="col-span-12 md:col-span-7">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Now you can</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-ink">
              Preserve, build, and
              <span className="italic text-tulsi"> celebrate </span>
              — all here.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-ink-50 text-base md:text-lg font-light leading-relaxed">
              Four tools, one archive. Every family record, portrait and card lives inside your private account —
              yours forever, and only ever shared on your terms.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-5 md:gap-6">
          {TOOLS.map((t, i) => (
            <motion.div
              key={t.key}
              data-testid={HOME_TOOLS.tile(t.key)}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className={`col-span-12 md:col-span-6 lg:col-span-3 rounded-2xl border border-ivory-300 overflow-hidden bg-white/70 group hover:border-saffron transition`}
            >
              <div className={`aspect-[4/3] w-full flex items-center justify-center ${t.tone}`}>
                {t.illustration}
              </div>
              <div className="p-6">
                <div className={`text-[10px] uppercase tracking-[0.24em] ${t.accent}`}>{t.eyebrow}</div>
                <div className="mt-2 font-serif text-2xl text-ink leading-tight">{t.title}</div>
                <p className="mt-3 text-sm text-ink-50 leading-relaxed">{t.body}</p>
                <Link
                  to={t.to}
                  className={`mt-5 inline-flex items-center gap-1 text-sm font-medium ${t.accent} group-hover:text-saffron transition`}
                >
                  {t.cta}
                  <span className="inline-block group-hover:translate-x-1 transition-transform">→</span>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CardSamplesStrip() {
  const [samples, setSamples] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const d = await cardsApi.samples();
        setSamples(d.samples);
      } catch (e) { /* silent */ }
    })();
  }, []);

  if (!samples.length) return null;

  return (
    <section
      data-testid={HOME_TOOLS.samples}
      className="py-24 md:py-32"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end mb-12">
          <div className="col-span-12 md:col-span-8">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">The card studio</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-ink">
              A keepsake for every
              <span className="italic text-tulsi"> passage </span>
              of a life.
            </h2>
            <p className="mt-6 max-w-2xl text-ink-50 text-base md:text-lg font-light leading-relaxed">
              Compose a birthday wish, a memorial, an anniversary card, a naming ceremony
              announcement — with AI-suggested words and any portrait you upload.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link
              to="/cards"
              className="inline-flex items-center gap-1 rounded-full bg-saffron text-ivory px-6 py-3 font-medium hover:bg-saffron-deep transition"
            >
              Open the card studio →
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
          {samples.slice(0, 4).map((s, i) => (
            <Link
              key={i}
              to="/cards"
              data-testid={HOME_TOOLS.sample(i)}
              className="group rounded-2xl border border-ivory-300 bg-white/70 overflow-hidden hover:border-saffron hover:shadow-[0_20px_40px_-24px_rgba(232,106,51,0.35)] transition"
            >
              <div className="aspect-[1024/1536] overflow-hidden bg-ivory-100">
                <img
                  src={cardsApi.sampleUrl(i)}
                  alt={s.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                />
              </div>
              <div className="px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-saffron">
                  {s.occasion.replace(/_/g, " ")}
                </div>
                <div className="mt-1 font-serif text-ink truncate">{s.title}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
